import React, { useCallback } from 'react';
import classNames from 'classnames';
import Modal, { ModalHeader, ModalTransition, ModalTitle, ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button';
import styled from '@emotion/styled';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { DynamicResultTable } from '@css-xp/base';
import { CloudProductDetailView } from '@css-xp/base/entitlement-utils';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { routePaths, SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';
import { usePersistedCacheQuery } from '@css-xp/ppc-custom-hooks';
import { emptyStateProp, errorStateProp } from '@css-xp/ppc-common';
import { TransactionAccountProduct } from '@css-xp/graphtypes/src/generated-types-new';
import { fetchLinkedSubscriptionsToBp } from '../queries/fetch-linked-subscription-to-bp';
import './linked-subscriptions-to-bp-table.scss';

const Loader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30vh;
`;

export const LinkedSubscriptionsToBillingProfileModal = ({
  invoiceGroupId,
  isOpen,
  onClose,
  transactionAccountId,
}: {
  invoiceGroupId: string | null | undefined;
  isOpen: boolean;
  onClose: () => void;
  transactionAccountId: string | null | undefined;
}) => {
  const { data, loading, error } = usePersistedCacheQuery(fetchLinkedSubscriptionsToBp, {
    variables: {
      id: transactionAccountId,
      searchOptions: {
        first: 9999,
        offset: 0,
      },
      filterOptions: [
        {
          key: 'not',
          value: JSON.stringify([
            SUBSCRIPTION_CONSTANTS.addOnsFilter,
            SUBSCRIPTION_CONSTANTS.appsFilter,
            SUBSCRIPTION_CONSTANTS.sandboxFilter,
          ]),
        },
        {
          key: 'invoiceGroupId',
          value: invoiceGroupId,
        },
      ],
    },
  });

  const hasNoEmptyState = data?.transactionAccount?.productSubscriptionsInPPC?.nodes?.length !== 0;

  const wrapInCell = (node: React.ReactElement | string) => {
    return <div>{node}</div>;
  };

  const cells = [
    {
      key: 'Product',
      content: wrapInCell('Product'),
    },
    {
      key: 'Entitlement ID',
      content: wrapInCell('Entitlement ID'),
    },
  ];

  const subscriptionsRoutePPC =
    transactionAccountId && getRouteUrl(routePaths.subscriptions, { txaId: transactionAccountId });

  const makeRows = useCallback(() => {
    return (data?.transactionAccount?.productSubscriptionsInPPC?.nodes ?? []).map(
      (obj: TransactionAccountProduct | null, index: number) => {
        const rowKey = `billing-admin-row-${index}`;
        return {
          key: rowKey,
          cells: [
            {
              key: `${rowKey}-details`,
              content: (
                <CloudProductDetailView
                  productId={obj?.productId as string}
                  productName={obj?.productName}
                  productLink={`${subscriptionsRoutePPC}${obj?.id}`}
                  isOpenInNewTab={true}
                />
              ),
            },
            {
              key: `${rowKey}-details`,
              content: (
                <a href={`${subscriptionsRoutePPC}${obj?.id}`} target="_blank">
                  {obj?.entitlementNumber}
                </a>
              ),
            },
          ],
        };
      }
    );
  }, [data]);

  return (
    <ModalTransition>
      {isOpen && (
        <Modal onClose={onClose}>
          <ModalHeader>
            <ModalTitle testId="warning-modal-heading">Review impact on subscriptions</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p>
              Review the subscription associated with this billing profile that will be affected by the update in the
              primary customer contact.
            </p>
            <div>
              {loading && (
                <Loader>
                  <LoadingIndicator />
                </Loader>
              )}
              {(data || error) && (
                <div className={classNames({ 'linked-entitlements-to-bp-container': hasNoEmptyState })}>
                  <div className="linked-entitlements-table" test-id="linked-entitlement-to-bp">
                    <DynamicResultTable
                      rows={[...makeRows()]}
                      loadingSpinnerSize="large"
                      boxShadow={false}
                      className="billing-admins-table"
                      head={{ cells }}
                      emptyStateProps={emptyStateProp}
                      errorStateProps={errorStateProp}
                      error={error?.message}
                      hideHeadOnEmptyState={true}
                    ></DynamicResultTable>
                  </div>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <div id="warning-confirm-footer">
              <Button appearance="primary" onClick={onClose} testId="warning-okay-btn">
                Okay
              </Button>
            </div>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
